import AuthenticatedLayout from '../../components/AuthenticatedLayout'
import { Link, router } from '@inertiajs/react'
import { useState } from 'react'
import Button from '../../components/Button'

function formatCurrency(amount) {
  return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount || 0)
}

export default function ActivitiesShow({ activity, products, product_stats, team_stats }) {
  const [statsTab, setStatsTab] = useState('products')

  function deleteActivity() {
    if (!confirm('Ta bort den här aktiviteten? Det går inte att ångra.')) return
    router.delete(`/activities/${activity.id}`)
  }

  const totalStores = team_stats.reduce((sum, t) => sum + t.stores_count, 0)
  const totalTeamRevenue = team_stats.reduce((sum, t) => sum + t.total_revenue, 0)
  const totalUnitsSold = product_stats.reduce((sum, p) => sum + p.units_sold, 0)
  const totalProductRevenue = product_stats.reduce((sum, p) => sum + p.total_revenue, 0)

  return (
    <AuthenticatedLayout>
      <div className="space-y-8">
        <div className="flex justify-between items-start">
          <div>
            <Link href="/activities" className="text-sm text-indigo-600 hover:underline">
              ← Aktiviteter
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 mt-2">{activity.name}</h1>
            {activity.description && (
              <p className="text-gray-500 mt-1">{activity.description}</p>
            )}
          </div>
          <div className="flex gap-2">
            <a href={`/activities/${activity.id}/export`}>
              <Button variant="secondary">Exportera underlag</Button>
            </a>
            <Link href={`/activities/${activity.id}/edit`}>
              <Button variant="secondary">Redigera</Button>
            </Link>
            <Button variant="danger" onClick={deleteActivity}>Ta bort</Button>
          </div>
        </div>

        {/* Products */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Produkter</h2>
            <Link href={`/activities/${activity.id}/products/new`}>
              <Button>Lägg till produkt</Button>
            </Link>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {products.length === 0 && (
              <p className="px-4 py-8 text-center text-gray-500 text-sm">
                Inga produkter ännu.
              </p>
            )}
            {products.map(p => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  {p.images && p.images.length > 0 && (
                    <img
                      src={p.images[0].url}
                      alt=""
                      className="w-10 h-10 object-cover rounded-md flex-shrink-0"
                    />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">{p.name}</p>
                    {p.description && (
                      <p className="text-sm text-gray-500 mt-0.5">{p.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-medium text-gray-900">{formatCurrency(p.price)}</span>
                  <Link
                    href={`/activities/${activity.id}/products/${p.id}/edit`}
                    className="text-sm text-indigo-600 hover:underline"
                  >
                    Redigera
                  </Link>
                  <Link
                    href={`/activities/${activity.id}/products/${p.id}`}
                    method="delete"
                    as="button"
                    className="text-sm text-red-600 hover:underline"
                    onClick={e => { if (!confirm('Ta bort den här produkten?')) e.preventDefault() }}
                  >
                    Ta bort
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Stats */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Statistik</h2>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
              <button
                onClick={() => setStatsTab('products')}
                className={`px-4 py-1.5 ${statsTab === 'products' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                Produkter
              </button>
              <button
                onClick={() => setStatsTab('teams')}
                className={`px-4 py-1.5 border-l border-gray-200 ${statsTab === 'teams' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                Lag
              </button>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {statsTab === 'products' && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-gray-500">
                    <th className="px-4 py-3 font-medium">Produkt</th>
                    <th className="px-4 py-3 font-medium text-right">Sålda enheter</th>
                    <th className="px-4 py-3 font-medium text-right">Försäljning</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {product_stats.map(p => (
                    <tr key={p.id}>
                      <td className="px-4 py-3 text-gray-900">{p.name}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{p.units_sold}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(p.total_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200 bg-gray-50 font-semibold">
                    <td className="px-4 py-3 text-gray-900">Totalt</td>
                    <td className="px-4 py-3 text-right text-gray-900">{totalUnitsSold}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{formatCurrency(totalProductRevenue)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
            {statsTab === 'teams' && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-gray-500">
                    <th className="px-4 py-3 font-medium">Lag</th>
                    <th className="px-4 py-3 font-medium text-right">Butiker</th>
                    <th className="px-4 py-3 font-medium text-right">Försäljning</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {team_stats.map(t => (
                    <tr key={t.id}>
                      <td className="px-4 py-3 text-gray-900">{t.name}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{t.stores_count}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(t.total_revenue)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200 bg-gray-50 font-semibold">
                    <td className="px-4 py-3 text-gray-900">Totalt</td>
                    <td className="px-4 py-3 text-right text-gray-900">{totalStores}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{formatCurrency(totalTeamRevenue)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  )
}
